package org.zq.astrock_ai.ui.research.gushi.data

import org.zq.astrock_ai.util.formatDecimal
import kotlin.math.abs
import kotlin.math.min

/** 单标的纸面流水汇总（FIFO，按各笔 quantity 股数配对）。 */
data class PaperSymbolSummary(
    val code: String,
    val name: String,
    val buySignals: Int,
    val sellSignals: Int,
    val matchedShares: Int,
    val avgBuy: Double,
    val avgSell: Double,
    val realizedPnl: Double,
    val openShares: Int,
) {
    /** 兼容旧字段名 */
    val matchedLots: Int get() = matchedShares
    val openLots: Int get() = openShares
}

private fun PaperTradeEntry.effectiveQty(fallbackLot: Int): Int =
    when {
        quantity > 0 -> quantity
        fallbackLot > 0 -> fallbackLot
        else -> 100
    }

fun summarizePaperTrades(
    entries: List<PaperTradeEntry>,
    lotSize: Int = 100,
): List<PaperSymbolSummary> {
    if (entries.isEmpty()) return emptyList()
    val byCode = entries
        .filter { it.price > 0 && (it.side == "BUY" || it.side == "SELL") }
        .groupBy { it.code.filter { ch -> ch.isDigit() }.takeLast(6) }
        .filterKeys { it.length == 6 }

    return byCode.map { (code, list) ->
        val chronological = list.sortedBy { it.atMs }
        val openBuys = ArrayDeque<Pair<Double, Int>>()
        var buySignals = 0
        var sellSignals = 0
        var buySum = 0.0
        var buyQtySum = 0
        var sellSum = 0.0
        var sellQtySum = 0
        var matchedShares = 0
        var realized = 0.0
        chronological.forEach { e ->
            val qty = e.effectiveQty(lotSize)
            when (e.side.uppercase()) {
                "BUY" -> {
                    buySignals++
                    buySum += e.price * qty
                    buyQtySum += qty
                    openBuys.addLast(e.price to qty)
                }
                "SELL" -> {
                    sellSignals++
                    sellSum += e.price * qty
                    sellQtySum += qty
                    var remain = qty
                    while (remain > 0 && openBuys.isNotEmpty()) {
                        val (buyPx, buyQty) = openBuys.removeFirst()
                        val take = min(remain, buyQty)
                        realized += (e.price - buyPx) * take
                        matchedShares += take
                        remain -= take
                        if (buyQty > take) {
                            openBuys.addFirst(buyPx to (buyQty - take))
                        }
                    }
                }
            }
        }
        PaperSymbolSummary(
            code = code,
            name = chronological.lastOrNull { it.name.isNotBlank() }?.name.orEmpty().ifBlank { code },
            buySignals = buySignals,
            sellSignals = sellSignals,
            matchedShares = matchedShares,
            avgBuy = if (buyQtySum > 0) buySum / buyQtySum else 0.0,
            avgSell = if (sellQtySum > 0) sellSum / sellQtySum else 0.0,
            realizedPnl = realized,
            openShares = openBuys.sumOf { it.second },
        )
    }.sortedByDescending { abs(it.realizedPnl) }
}

fun exportPaperSummaryText(summaries: List<PaperSymbolSummary>, lotSize: Int): String = buildString {
    appendLine("代码,名称,买信号,卖信号,配对股数,均买,均卖,已实现盈亏,未平股数")
    summaries.forEach { s ->
        appendLine(
            listOf(
                s.code,
                s.name.replace(',', ' '),
                s.buySignals,
                s.sellSignals,
                s.matchedShares,
                formatDecimal(s.avgBuy, 3),
                formatDecimal(s.avgSell, 3),
                formatDecimal(s.realizedPnl),
                s.openShares,
            ).joinToString(","),
        )
    }
    appendLine()
    appendLine("说明: FIFO 按各笔手数配对；无手数记录时回退 ${lotSize} 股。")
}
